import React, { FC } from 'react';
import styled, { css } from 'styled-components';
import { Kfh_ContactDetailsType } from '../../types/graphqlTypes';
import { PROPERTY_URLS } from '../../urls';
import { Button } from '../Button';
import { Typography } from '../Typography';
import { LinkList } from '../LinkList';
import { IconLink } from '../IconLink';

interface BranchDivisionSummaryProps {
  contactDetails: Kfh_ContactDetailsType;
  type: 'sales' | 'lettings';
  mapLink: string;
  name: string;
  businessId?: number;
  contactCTA?: boolean;
  contactCTAOnClick?: any;
  isLettingsOverride?: boolean;
  isLettingsOverrideAreaRelativeUrl?: string;
}

const StyledBranchDivisionSummaryHeading = styled(Typography)`
  ${({ theme: { space, media } }) => css`
    margin-bottom: ${space.md};

    // Tablet > styles

    @media (min-width: ${media.md}) {
    }
  `};
`;

const StyledBranchDivisionSummaryContact = styled.div`
  ${({ theme: { space } }) => css`
    margin-bottom: ${space.md};

    a {
      display: block;
    }
  `};
`;

const StyledBranchDivisionSummaryAddress = styled.div`
  ${({ theme: { space, media } }) => css`
    margin: ${space.md} 0;

    address {
      margin-bottom: ${space.sm};
    }

    // Tablet > styles

    @media (min-width: ${media.md}) {
      margin: ${space.lg} 0;
    }
  `};
`;

const generatePropertyUrl = ({
  type: categoryType,
  businessId,
  sold,
  isLettingsOverride,
  isLettingsOverrideAreaRelativeUrl,
}: {
  type: string;
  businessId: number;
  sold: boolean;
  isLettingsOverride?: boolean;
  isLettingsOverrideAreaRelativeUrl?: string;
}) => {
  // handle letting override
  if (isLettingsOverride && categoryType !== 'sales') {
    return `${isLettingsOverrideAreaRelativeUrl}${
      sold ? PROPERTY_URLS.propertiesLet : PROPERTY_URLS.propertiesForRent
    }`;
  }

  if (!categoryType || !businessId) {
    return '';
  }
  const base = `/search-results/?multiSearch=business%3D${businessId}${
    categoryType === 'sales' ? '' : '&category=RENTAL'
  }${sold ? '&unavailable=true&sort=DATE' : ''}`;
  return base;
};

export const BranchDivisionSummary: FC<BranchDivisionSummaryProps> = ({
  type,
  contactDetails,
  mapLink,
  name,
  businessId,
  contactCTA,
  contactCTAOnClick,
  isLettingsOverride,
  isLettingsOverrideAreaRelativeUrl,
}) => {
  const handleContactCTAClick = () => {
    contactCTAOnClick();
  };

  return (
    <div>
      <StyledBranchDivisionSummaryHeading component="h2" variant="titleMedium">
        {type === 'sales' ? 'Sales' : 'Lettings'}
      </StyledBranchDivisionSummaryHeading>

      {/* Contact details */}
      <StyledBranchDivisionSummaryContact>
        {contactDetails?.telephone && (
          <a href={`tel:${contactDetails?.telephone?.replace(/\s/g, '')}`}>{contactDetails?.telephone}</a>
        )}
        {contactDetails?.email && <a href={`mailto:${contactDetails?.email}`}>{contactDetails?.email}</a>}
      </StyledBranchDivisionSummaryContact>

      {/* Contact CTA */}
      {contactCTA && (
        <Button as="button" type="button" variant="outline" onClick={handleContactCTAClick}>
          Contact {type} team
        </Button>
      )}

      {/* Address */}
      <StyledBranchDivisionSummaryAddress>
        {contactDetails && contactDetails.streetname ? (
          <address>
            {contactDetails.streetname}
            <br />
            {contactDetails.city} {contactDetails.postcode}
          </address>
        ) : (
          ''
        )}
        <IconLink arrowRight url={mapLink} target="_blank" rel="noreferrer" label="Find us on Google maps" />
      </StyledBranchDivisionSummaryAddress>

      {/* Property links */}
      {businessId && (
        <LinkList
          iconRight
          items={[
            {
              link: {
                url: generatePropertyUrl({
                  type,
                  businessId,
                  sold: false,
                  isLettingsOverride,
                  isLettingsOverrideAreaRelativeUrl,
                }),

                name: `Properties for ${type === 'sales' ? 'sale' : 'rent'} in ${name}`,
              },
            },
            {
              link: {
                name: `${type === 'sales' ? 'Sold' : 'Let'} properties in ${name}`,
                url: generatePropertyUrl({
                  type,
                  businessId,
                  sold: true,
                  isLettingsOverride,
                  isLettingsOverrideAreaRelativeUrl,
                }),
              },
            },
          ]}
        />
      )}
    </div>
  );
};

export default BranchDivisionSummary;
